import { createReducer, on } from '@ngrx/store';
import {
  clearSubmission,
  deleteReviewMaterialDocuments,
  getSubmissionSitesSuccess,
  loadingSubmission,
  loadingSubmissionList,
  loadingWorkspaceTypes,
  setReviewMaterialDocuments,
  storeActiveSubmissionId,
  storeLoadingReviewMaterials,
  storeMaterialDocumentTypes,
  storeReviewMaterials,
  storeSubmission,
  storeSubmissionList,
  storeWorkspaceTypes,
  updateStoreReviewMaterials,
  updatingReviewerDocumentList,
  uploadingReviewerDocument,
} from './submission.actions';
import { Submission } from '../models/submission.model';
import { WorkspaceType } from '@admin/questionnaire/questionnaire.service';
import { Thread } from '@data-access/submissions';

export const featureKey = 'submission';

export interface EntityLoadingState {
  loading: boolean;
  id?: string;
}

export interface SubmissionState {
  submissionList: Submission[];
  activeSubmissionId?: string;
  submission?: Submission;
  loadingSubmission: EntityLoadingState;
  loadingSubmissionList: boolean;
  workspaceTypes: WorkspaceType[];
  loadingWorkspaceTypes: boolean;
  hasNotificationsDetails?: boolean;
  hasNotificationsDocuments?: boolean;
  threads?: any[];
  loadingMessages?: boolean;
  activeThread?: Thread;
  reviewMaterialDocuments?: any[];
  loadingReviewMaterialDocuments: boolean;
  requiredMaterialDocumentTypes?: any[];
  loadingRequiredMaterialDocumentTypes?: boolean;
  uploadingReviewerDocument: {
    [entityId: string]: {
      [documentId: string]: any;
    };
  };
  sites?: any[];
  sitesStatuses?: any[];
  studySites?: any[];
}

export const initialState: SubmissionState = {
  submissionList: [],
  submission: undefined,
  loadingSubmission: {
    loading: false,
  },
  loadingSubmissionList: false,
  workspaceTypes: [],
  loadingWorkspaceTypes: false,
  threads: [],
  reviewMaterialDocuments: [],
  loadingReviewMaterialDocuments: false,
  requiredMaterialDocumentTypes: [],
  loadingRequiredMaterialDocumentTypes: false,
  uploadingReviewerDocument: {},
  activeThread: undefined,
  hasNotificationsDetails: false,
  hasNotificationsDocuments: false,
};

export const submissionReducer = createReducer(
  initialState,
  on(storeActiveSubmissionId, (state, { id }) => ({
    ...state,
    activeSubmissionId: id,
  })),
  on(storeSubmission, (state, { submission }) => ({
    ...state,
    submission,
    loadingSubmission: {
      loading: false,
    },
  })),
  on(storeSubmissionList, (state, { submissions }) => ({
    ...state,
    submissionList: submissions,
    loadingSubmissionList: false,
  })),
  on(loadingSubmission, (state, { id }) => ({
    ...state,
    loadingSubmission: {
      loading: true,
      id,
    },
  })),
  on(loadingSubmissionList, (state) => ({
    ...state,
    loadingSubmissionList: true,
  })),
  on(storeWorkspaceTypes, (state, { workspaceTypes }) => ({
    ...state,
    loadingWorkspaceTypes: false,
    workspaceTypes,
  })),
  on(loadingWorkspaceTypes, (state) => ({
    ...state,
    loadingWorkspaceTypes: true,
  })),
  on(storeReviewMaterials, (state, { documents }) => ({
    ...state,
    reviewMaterialDocuments: documents,
    loadingReviewMaterialDocuments: false,
  })),
  on(updateStoreReviewMaterials, (state: SubmissionState, { documents }) => ({
    ...state,
    reviewMaterialDocuments: [...documents, ...state.reviewMaterialDocuments!],
    loadingReviewMaterialDocuments: false,
  })),
  on(storeLoadingReviewMaterials, (state) => ({
    ...state,
    loadingReviewMaterialDocuments: true,
  })),

  on(storeMaterialDocumentTypes, (state, { documentTypes }) => ({
    ...state,
    requiredMaterialDocumentTypes: documentTypes,
  })),

  on(setReviewMaterialDocuments, (state, { document }) => {
    const updatedDocuments = state?.reviewMaterialDocuments?.map((doc) =>
      doc.id === document.id ? document : doc,
    );
    return {
      ...state,
      reviewMaterialDocuments: updatedDocuments,
    };
  }),
  on(deleteReviewMaterialDocuments, (state, { documentId }) => {
    const updatedDocuments = state?.reviewMaterialDocuments?.filter(
      (doc) => doc.id !== documentId,
    );
    return {
      ...state,
      reviewMaterialDocuments: updatedDocuments,
    };
  }),
  on(updatingReviewerDocumentList, (state, { document }) => {
    const updatedDocuments = state?.reviewMaterialDocuments ?? [];
    const reviewMaterialDocuments = [...updatedDocuments];
    reviewMaterialDocuments.unshift(document);
    return {
      ...state,
      reviewMaterialDocuments,
    };
  }),
  on(
    uploadingReviewerDocument,
    (
      state,
      {
        uuid,
        workspaceId,
        filename,
        reviewDocumentTypeId,
        reviewDocumentTypeName,
      },
    ) => ({
      ...state,
      uploadingReviewerDocument: {
        ...state.uploadingReviewerDocument,
        [workspaceId]: {
          ...state.uploadingReviewerDocument[workspaceId],
          [uuid]: {
            id: uuid,
            uploading: true,
            progress: 0,
            name: filename,
            error: null,
            reviewDocumentTypeId,
            reviewDocumentTypeName,
          },
        },
      },
    }),
  ),
  on(getSubmissionSitesSuccess, (state, { sites }) => ({
    ...state,
    sites,
  })),
  on(clearSubmission, (state) => ({
    ...state,
    submission: undefined,
  })),
);
